import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { useToast } from "../components/ui/use-toast";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Module } from "../types/module";
import { moduleService } from "../services/moduleService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Eye, Pencil, Plus, Users, Edit, X } from "lucide-react";
import { MappingComponent } from "../components/modules/MappingComponent";
import { Spinner } from "../components/ui/Spinner";
import { useUser } from '../contexts/UserContext';
import { Input } from "../components/ui/input";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { parseISO, format, isAfter, isBefore, isWithinInterval } from "date-fns";
import { Badge } from "../components/ui/badge";
import { ExportExcel } from '../components/ui/ExportExcel';

export function ModuleManagement() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState("view");
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();
  const [filterModuleName, setFilterModuleName] = useState("");
  const [filterStartDate, setFilterStartDate] = useState<string>("");
  const [filterEndDate, setFilterEndDate] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await moduleService.getAllModules();
      setModules(data);
    } catch (err) {
      setError('Failed to fetch modules. Please try again later.');
      toast({
        title: "Error",
        description: "Failed to fetch modules. Please try again later.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (moduleId: number) => {
    console.log('Navigating to edit module:', moduleId);
    navigate(`/modules/${moduleId}/edit`);
  };

  // Filtered modules
  const today = new Date();
  const filteredModules = modules.filter(module => {
    const matchesName = filterModuleName === '' || module.name.toLowerCase().includes(filterModuleName.toLowerCase());
    let matchesStart = true;
    if (filterStartDate) {
      if (module.startDate) {
        const moduleStart = parseISO(module.startDate);
        const filterStart = parseISO(filterStartDate);
        matchesStart = moduleStart >= filterStart;
      } else {
        matchesStart = false;
      }
    }
    let matchesEnd = true;
    if (filterEndDate) {
      if (module.endDate) {
        const moduleEnd = parseISO(module.endDate);
        const filterEnd = parseISO(filterEndDate);
        matchesEnd = moduleEnd <= filterEnd;
      } else {
        matchesEnd = false;
      }
    }
    let matchesStatus = true;
    if (filterStatus === 'future') {
      matchesStatus = !!module.startDate && isAfter(parseISO(module.startDate), today);
    } else if (filterStatus === 'past') {
      matchesStatus = !!module.endDate && isBefore(parseISO(module.endDate), today);
    } else if (filterStatus === 'active') {
      matchesStatus = !!module.startDate && !!module.endDate && isWithinInterval(today, { start: parseISO(module.startDate), end: parseISO(module.endDate) });
    }
    return matchesName && matchesStart && matchesEnd && matchesStatus;
  });

  // Helper to get status string
  const getStatusString = (module: Module) => {
    if (module.startDate && isAfter(parseISO(module.startDate), today)) {
      return 'Future';
    } else if (module.endDate && isBefore(parseISO(module.endDate), today)) {
      return 'Past';
    } else if (module.startDate && module.endDate && isWithinInterval(today, { start: parseISO(module.startDate), end: parseISO(module.endDate) })) {
      return 'Active';
    } else {
      return '-';
    }
  };

  // Prepare export data with formatted status and dates
  const exportModules = filteredModules.map(module => ({
    sequenceNumber: module.sequenceNumber,
    name: module.name,
    description: module.description,
    sellerType: module.sellerType,
    status: getStatusString(module),
    startDate: module.startDate ? format(parseISO(module.startDate), 'dd/MM/yyyy') : '-',
    endDate: module.endDate ? format(parseISO(module.endDate), 'dd/MM/yyyy') : '-',
  }));

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <Spinner label="Loading modules..." />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardContent className="flex items-center justify-center h-32">
            <p className="text-red-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Module Management</CardTitle>
          <div className="flex gap-2">
          {currentUser?.role === 'corporate_admin' && (
          <Button onClick={() => navigate('/modules/new')}>
            <Plus className="mr-2 h-4 w-4" />
            Create New Module
          </Button>
          )}
            <ExportExcel
              data={exportModules}
              columns={[
                { label: 'Sequence', key: 'sequenceNumber' },
                { label: 'Name', key: 'name' },
                { label: 'Description', key: 'description' },
                { label: 'Seller Type', key: 'sellerType' },
                { label: 'Status', key: 'status' },
                { label: 'Start Date', key: 'startDate' },
                { label: 'End Date', key: 'endDate' },
              ]}
              filename="modules.xlsx"
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Module Name Filter and Count */}
          <div className="flex flex-wrap items-end gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium mb-1">Module Name</label>
              <Input
                value={filterModuleName}
                onChange={e => setFilterModuleName(e.target.value)}
                placeholder="Filter by module name"
                className="w-40"
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                className="w-32 border rounded h-9 px-2 text-sm"
              >
                <option value="">All</option>
                <option value="active">Active</option>
                <option value="past">Past</option>
                <option value="future">Future</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">Start Date</label>
              <DatePicker
                selected={filterStartDate ? parseISO(filterStartDate) : null}
                onChange={(date: Date | null) => setFilterStartDate(date ? format(date, "yyyy-MM-dd") : "")}
                dateFormat="yyyy-MM-dd"
                className="flex h-9 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                placeholderText="Select start date"
                isClearable
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1">End Date</label>
              <DatePicker
                selected={filterEndDate ? parseISO(filterEndDate) : null}
                onChange={(date: Date | null) => setFilterEndDate(date ? format(date, "yyyy-MM-dd") : "")}
                dateFormat="yyyy-MM-dd"
                className="flex h-9 w-40 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                placeholderText="Select end date"
                isClearable
                minDate={filterStartDate ? parseISO(filterStartDate) : undefined}
              />
            </div>
            <Button
              variant="outline"
              className="h-9"
              onClick={() => {
                setFilterModuleName('');
                setFilterStartDate('');
                setFilterEndDate('');
                setFilterStatus('');
              }}
              disabled={
                !filterModuleName && !filterStartDate && !filterEndDate && !filterStatus
              }
            >
              <X className="mr-2 h-4 w-4" />
              Clear Filters
            </Button>
            <div className="ml-auto text-sm font-medium">
              Count: {filteredModules.length}
            </div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
          <Table>
            <TableHeader>
              <TableRow>
                  <TableHead>Sequence</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Seller Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>End Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
                {filteredModules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500 py-8">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredModules.map((module) => (
                <TableRow key={module.id}>
                      <TableCell>{module.sequenceNumber}</TableCell>
                  <TableCell className="font-medium">{module.name}</TableCell>
                  <TableCell>{module.description}</TableCell>
                  <TableCell className="capitalize">{module.sellerType}</TableCell>
                      <TableCell>
                        {(() => {
                          if (module.startDate && isAfter(parseISO(module.startDate), today)) {
                            return <Badge className="bg-blue-100 text-blue-800">Future</Badge>;
                          } else if (module.endDate && isBefore(parseISO(module.endDate), today)) {
                            return <Badge className="bg-gray-100 text-gray-800">Past</Badge>;
                          } else if (module.startDate && module.endDate && isWithinInterval(today, { start: parseISO(module.startDate), end: parseISO(module.endDate) })) {
                            return <Badge className="bg-green-100 text-green-800">Active</Badge>;
                          } else {
                            return <Badge>-</Badge>;
                          }
                        })()}
                      </TableCell>
                      <TableCell>{module.startDate ? module.startDate.slice(0, 10) : '-'}</TableCell>
                      <TableCell>{module.endDate ? module.endDate.slice(0, 10) : '-'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {currentUser?.role === 'corporate_admin' && (
                        <>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(module.id)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                        </>
                      )}
                      {currentUser?.role === 'distributor_admin' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/modules/${module.id}/mapping`)}
                        title="Map Users"
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                  ))
                )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 